window.theme = window.theme || {};
theme.initWhenVisible = function (options) {
  const threshold = options.threshold ? options.threshold : 0;

  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        if (typeof options.callback === 'function') {
          options.callback();
          observer.unobserve(entry.target);
        }
      }
    });
  }, { rootMargin: `0px 0px ${threshold}px 0px` });

  observer.observe(options.element);
};

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  const currentScrollX = window.scrollX;
  const currentScrollY = window.scrollY;

  try {
    if (elementToFocus.focus) {
      elementToFocus.focus({ preventScroll: true });
    } else {
      elementToFocus.focus();
    }
  } catch (err) {
    elementToFocus.focus();
  }

  if (window.scrollX !== currentScrollX || window.scrollY !== currentScrollY) {
    window.scrollTo(currentScrollX, currentScrollY);
  }

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

// Shared iOS-safe scroll lock. `overflow: hidden` on body alone doesn't stop
// touch scrolling on iOS Safari - position: fixed is the reliable fix, but
// several overlays (mega menu, search modal, filter drawer) can be open at
// once. Without reference counting, closing the last-opened overlay wipes
// body's lock styles even while an earlier overlay is still open, letting
// the page scroll behind it. Each holder locks/unlocks with its own id;
// the lock/scroll position is only applied on the first holder and only
// released once every holder has unlocked.
window.ScrollLock = {
  _holders: new Set(),
  _scrollY: 0,
  // True for one tick around unlock()'s own window.scrollTo() restore below -
  // lets scroll-position-driven listeners elsewhere (e.g. sticky-header.js's
  // big-logo shrink-on-scroll) tell "the page is genuinely being scrolled by
  // the user" apart from "ScrollLock is silently restoring where it already
  // was", so they don't replay their transition/animation for a scroll that
  // never really happened from the user's point of view.
  _restoring: false,

  lock(id) {
    if (this._holders.size === 0) {
      this._scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this._scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    }
    this._holders.add(id);
  },

  unlock(id) {
    this._holders.delete(id);
    if (this._holders.size > 0) return;

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    this._restoring = true;
    window.scrollTo(0, this._scrollY || 0);
    // Double rAF: the 'scroll' event from the scrollTo() above is dispatched
    // asynchronously and isn't guaranteed to land within a single frame -
    // clearing after only one rAF risked the flag flipping false a moment
    // before that event actually arrives, letting it slip through unguarded.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this._restoring = false;
      });
    });
  },

  isLocked() {
    return this._holders.size > 0;
  },
};

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.input.addEventListener('input', this.resizeInput.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
    this.resizeInput();
  }

  resizeInput() {
    const len = String(this.input.value).length || 1;
    this.input.style.width = len + 'ch';
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
    this.resizeInput();

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('.mobile-facets__details[open]').forEach((details) => {
      details.classList.add('accordion-open');
    });

    this.querySelectorAll('summary').forEach((summary) => {
      if (summary.dataset.menuListenerBound) return;
      summary.dataset.menuListenerBound = 'true';
      summary.addEventListener('click', this.onSummaryClick.bind(this));
    });
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => {
      if (button.dataset.menuListenerBound) return;
      button.dataset.menuListenerBound = 'true';
      button.addEventListener('click', this.onCloseButtonClick.bind(this));
    });

    // Click on the dimmed backdrop (the .mobile-facets overlay itself, not the
    // drawer panel inside it) closes the filter drawer.
    const backdrop = this.querySelector('.mobile-facets');
    if (backdrop && !backdrop.dataset.backdropListenerBound) {
      backdrop.dataset.backdropListenerBound = 'true';
      backdrop.addEventListener('click', (event) => {
        if (event.target !== backdrop) return; // ignore clicks inside the panel
        const summary = this.mainDetailsToggle.querySelector('summary');
        this.closeMenuDrawer(event, summary);
      });
    }
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 1023px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else if (detailsElement.classList.contains('mobile-facets__details')) {
      event.preventDefault();
      this.toggleMobileFacetAccordion(detailsElement, summaryElement);
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        document.body.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  toggleMobileFacetAccordion(detailsElement, summaryElement) {
    if (detailsElement._accordionTimer) {
      clearTimeout(detailsElement._accordionTimer);
      detailsElement._accordionTimer = null;
    }

    const isOpen = detailsElement.classList.contains('accordion-open');
    if (isOpen) {
      detailsElement.classList.remove('accordion-open');
      summaryElement.setAttribute('aria-expanded', 'false');
      const submenu = detailsElement.querySelector('.mobile-facets__submenu');
      const duration = submenu
        ? parseFloat(getComputedStyle(submenu).transitionDuration) * 1000
        : 350;
      detailsElement._accordionTimer = setTimeout(() => {
        detailsElement.removeAttribute('open');
        detailsElement._accordionTimer = null;
      }, duration);
    } else {
      detailsElement.setAttribute('open', '');
      summaryElement.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() =>
        requestAnimationFrame(() => detailsElement.classList.add('accordion-open'))
      );
    }
  }

  openMenuDrawer(summaryElement) {
    // Freeze the sticky header BEFORE the scroll-lock so the scroll-lock can't
    // trigger its hide-on-scroll behaviour. Header keeps its current state.
    const stickyHeader = document.querySelector('sticky-header');
    if (stickyHeader) {
      stickyHeader._filterPrevHide = stickyHeader.preventHide;
      stickyHeader.preventHide = true;
    }

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
      document.body.classList.add('menu-opening');
      document.body.classList.add('filter-opening');
    });

    // Ensure Sort By section is expanded by default when filter drawer opens
    const sortByDetails = this.querySelector('.mobile-facets__sort-section');
    if (sortByDetails) {
      sortByDetails.setAttribute('open', '');
      sortByDetails.classList.add('accordion-open');
      const sortSummary = sortByDetails.querySelector('summary');
      if (sortSummary) sortSummary.setAttribute('aria-expanded', 'true');
    }

    summaryElement.setAttribute('aria-expanded', true);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);

    // Portal the filter overlay (.mobile-facets = dim backdrop + drawer panel) to
    // <body> so it escapes the product-grid stacking context. Only then does its
    // z-index:99999 paint OVER the header — the header shows dimmed behind it.
    const overlay = this.querySelector('.mobile-facets');
    if (overlay && overlay.parentElement !== document.body) {
      this._overlayHome = { parent: overlay.parentElement, next: overlay.nextElementSibling };
      document.body.appendChild(overlay);
      trapFocus(overlay, summaryElement);
    } else {
      trapFocus(this.mainDetailsToggle, summaryElement);
    }
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    document.body.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
      details.classList.remove('accordion-open');
      setTimeout(() => {
       document.body.classList.remove('filter-opening');
      }, 300);
      document.body.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    // Restore the sticky header's hide-on-scroll behaviour.
    const stickyHeader = document.querySelector('sticky-header');
    if (stickyHeader) {
      stickyHeader.preventHide = stickyHeader._filterPrevHide || false;
    }

    // Move the portaled overlay back into the drawer after the close animation.
    if (this._overlayHome) {
      const home = this._overlayHome;
      this._overlayHome = null;
      const overlay = Array.from(document.body.children).find((el) => el.classList && el.classList.contains('mobile-facets'));
      if (overlay) {
        setTimeout(() => {
          if (home.next && home.next.parentElement === home.parent) {
            home.parent.insertBefore(overlay, home.next);
          } else {
            home.parent.appendChild(overlay);
          }
        }, 300);
      }
    }

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    document.body.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();

    // Other scripts close this menu directly (e.g. predictive-search.js
    // calls detailsAnimated.shrink() / removeAttribute('open') when the
    // search field rendered inside this drawer is focused) instead of going
    // through closeMenuDrawer(). Those paths never released the scroll lock,
    // sticky-header freeze, or resize listener, leaving the page stuck
    // unscrollable after search closed. Watching the `open` attribute
    // catches every close path uniformly and runs the same cleanup.
    this._menuOpen = false;
    if (this.mainDetailsToggle) {
      this._openObserver = new MutationObserver(() => {
        const isOpen = this.mainDetailsToggle.hasAttribute('open');
        if (isOpen) {
          this._menuOpen = true;
        } else if (this._menuOpen) {
          this._menuOpen = false;
          this.releaseMenuLock();
        }
      });
      this._openObserver.observe(this.mainDetailsToggle, { attributes: true, attributeFilter: ['open'] });
    }
  }

  releaseMenuLock() {
    if (this.header) {
      this.header.classList.remove('menu-open');
      this.header.style.removeProperty('height');
      this.header.style.removeProperty('min-height');
    }
    const stickyHeaderEl = document.querySelector('sticky-header');
    if (stickyHeaderEl && this._headerPinStyles) {
      const s = this._headerPinStyles;
      ['position', 'top', 'left', 'right', 'width'].forEach((prop) => {
        if (s[prop]) stickyHeaderEl.style.setProperty(prop, s[prop]);
        else stickyHeaderEl.style.removeProperty(prop);
      });
      this._headerPinStyles = null;
    }
    window.removeEventListener('resize', this.onResize);
    window.ScrollLock.unlock('header-drawer');
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    document.body.classList.remove('menu-opening');
    this.mainDetailsToggle.classList.remove('menu-opening');

    const stickyHeader = document.querySelector('sticky-header');
    if (stickyHeader) {
      stickyHeader.preventHide = stickyHeader._headerDrawerPrevHide || false;
    }
  }

  openMenuDrawer(summaryElement) {
    // Freeze the sticky header's hide-on-scroll BEFORE reading its position.
    // The mega menu is positioned relative to the header (top: 100%), so if
    // residual scroll momentum (e.g. iOS inertial scroll still running from
    // the tap that opened this menu) triggers the header's hide-on-scroll
    // transform after the menu opens, the header - and the menu anchored to
    // it - slides up out of view, cutting the menu off at the top.
    const stickyHeader = document.querySelector('sticky-header');
    if (stickyHeader) {
      stickyHeader._headerDrawerPrevHide = stickyHeader.preventHide;
      stickyHeader.preventHide = true;
    }

    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;

    // Add `menu-open` FIRST - if the header is currently scrolled-hidden, the
    // theme's own `.shopify-section-header-hidden.menu-open { top: 0 }` rule
    // snaps it back into view. Measuring the header's position before that
    // (the previous order) read its hidden, off-screen bounding rect, so the
    // menu's height/offset got computed from the wrong position and the menu
    // rendered cut off at the top whenever it was opened right after a
    // scroll-hide.
    this.header.classList.add('menu-open');
    const headerRect = this.header.getBoundingClientRect();
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(headerRect.bottom - this.borderOffset)}px`
    );

    // Double rAF, not setTimeout(fn, 0): the slide-in transition only plays if
    // the browser has actually painted the closed (translateX(-100%)) state
    // before .menu-opening flips it to open - a bare setTimeout can fire
    // before that paint happens, especially with extra synchronous work (the
    // getBoundingClientRect() calls above force a layout reflow) landing in
    // the same tick, which is what made this intermittent rather than always
    // broken. The first rAF runs after the current paint is queued but before
    // it's guaranteed done; the second rAF runs after that paint has actually
    // happened, so the class flip is reliably a second, separate frame.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.mainDetailsToggle.classList.add('menu-opening');
        document.body.classList.add('menu-opening');
      });
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    window.ScrollLock.lock('header-drawer');

    // ScrollLock (just above) freezes <body> to position:fixed - the iOS-safe
    // scroll-lock trick - to stop background scrolling while the drawer is
    // open. But position:sticky needs an actually-scrolling ancestor to react
    // to; once body is frozen (no more scroll events), the header stops
    // "sticking" and renders at its plain in-flow document position instead,
    // which after scrolling down sits far above the viewport (confirmed via
    // devtools: header rect.y went to -579 while .menu-open/.scrolled-past-
    // header etc. were all in their normal states - this is the frozen-body
    // effect itself, not a missing class). Existing CSS already has two
    // gated `:has()` rules pinning the header fixed for specific scrolled
    // states, but a mid-reveal PDP-parallax scroll can reach this frozen
    // state without tripping either gate. Pinning here at headerRect (already
    // captured above, before this freeze) instead covers every case
    // unconditionally: at rest it's a no-op (top already 0), scrolled it
    // preserves wherever the header currently, correctly, is.
    //
    // ...except it isn't a true no-op even at top:0 - forcing position:sticky
    // to position:fixed (and back on close) repaints/recomposites the header
    // regardless of whether its coordinates actually change, which showed up
    // as a visible blink on the solid (non-transparent) header at rest. The
    // pin only exists to compensate for ScrollLock's scrollY→0 collapse
    // disagreeing with where a SCROLLED sticky header currently sits; at
    // scrollY 0 there's nothing to compensate for (collapsing 0 to 0 changes
    // nothing), so skip the pin entirely rather than doing free-but-not-free
    // work that only pays off once actually scrolled.
    if (window.scrollY > 0) {
      // this.header (.section-header) is the Shopify section wrapper - a body
      // grid/flow item. Pinning IT to position:fixed pulls it out of the flow
      // entirely, so its space collapses and every section below snaps up by
      // one header-height the instant the drawer opens (the homepage "back
      // screen" jump on real iPhones). Same bug, same fix as pdp-info-sidebar.js:
      // lock .section-header's own height so its flow space stays reserved, and
      // only make the inner <sticky-header> element position:fixed so IT pins
      // to the real viewport without disturbing the outer layout.
      this.header.style.setProperty('height', `${headerRect.height}px`, 'important');
      this.header.style.setProperty('min-height', `${headerRect.height}px`, 'important');
      if (stickyHeader) {
        this._headerPinStyles = {
          position: stickyHeader.style.position,
          top: stickyHeader.style.top,
          left: stickyHeader.style.left,
          right: stickyHeader.style.right,
          width: stickyHeader.style.width,
        };
        stickyHeader.style.setProperty('position', 'fixed', 'important');
        stickyHeader.style.setProperty('top', `${headerRect.top}px`, 'important');
        stickyHeader.style.setProperty('left', '0', 'important');
        stickyHeader.style.setProperty('right', '0', 'important');
        stickyHeader.style.setProperty('width', '100%', 'important');
      }
    }

    this._menuOpen = true;
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this._menuOpen = false;
    this.releaseMenuLock();
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    // window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    // window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('.product__media-toggle');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    // window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          const existingRail = this.querySelector('tabbed-product-rail');
          const incomingSimilarWrapper = recommendations.querySelector('[data-rail-swiper="similar"] .swiper-wrapper');
          const existingSimilarWrapper = this.querySelector('[data-rail-swiper="similar"] .swiper-wrapper');

          if (existingRail && incomingSimilarWrapper && existingSimilarWrapper) {
            // Surgical update: only the similar-rail slides are new in this fetch.
            // Replacing the whole innerHTML would tear down the live
            // tabbed-product-rail, re-running its connectedCallback — which
            // refetches every recently-viewed card and rebuilds the swipers
            // mid-interaction (visible reload of labels/wishlist/arrows).
            existingSimilarWrapper.innerHTML = incomingSimilarWrapper.innerHTML;

            const incomingEmpty = recommendations.querySelector('[data-panel="similar"] .related-products__empty');
            const existingEmpty = this.querySelector('[data-panel="similar"] .related-products__empty');
            if (incomingEmpty && existingEmpty) existingEmpty.hidden = incomingEmpty.hidden;

            existingRail.refreshRail?.('similar');
          } else {
            this.innerHTML = recommendations.innerHTML;
          }
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}


//  ANNOUNCEMENT BAR 

class announcementScroll extends HTMLElement {
  constructor() {
    super();
    this.config = {
      moveTime: parseFloat(this.dataset.speed), 
      space: 100, 
    };
    this.promotion = this.querySelector('.promotion');
  }

}

customElements.define('announcement-scroll', announcementScroll);

let announcementSwipers = [];

function initAnnouncementSwipers() {
  // Destroy existing swipers before re-init
  announcementSwipers.forEach(swiper => swiper.destroy(true, true));
  announcementSwipers = [];

  const announcements = document.querySelectorAll(".js-announcement-carousel");

  // Reset CSS vars
  document.documentElement.style.setProperty('--annoouncement-height', `0px`);
  document.documentElement.style.setProperty('--anouncement-scroll-height', `0px`);
  const announcementBar = document.querySelector('.utility-bar');
  if (announcementBar) {
    document.documentElement.style.setProperty('--anouncement-scrolling-height', `${announcementBar.offsetHeight}px`);
    document.documentElement.style.setProperty('--anouncement-scroll-height', `${announcementBar.offsetHeight}px`);
  }
  else{
      document.documentElement.style.setProperty('--anouncement-scrolling-height', `0`);
  }

  announcements.forEach(function (el, index) {
    el.classList.add(`s${index}`);      

    const swiper = new Swiper(`.js-announcement-carousel.s${index}`, {
      slidesPerView: 1,
      autoHeight: false,
      loop: true,
      effect: "fade",
      autoplay: (el.dataset.autoplay === 'true')
        ? { 
            delay: parseInt(el.dataset.speed) || 3000,
            disableOnInteraction: false
          }
        : false,
      speed: 300,
      on: {
        afterInit: function () {
          const announcementBar = document.querySelector('.utility-bar');
          if (announcementBar) {
            document.documentElement.style.setProperty('--annoouncement-height', `${announcementBar.offsetHeight}px`);
            document.documentElement.style.setProperty('--anouncement-scroll-height', `${announcementBar.offsetHeight}px`);
          }
        }
      }
    });

    announcementSwipers.push(swiper);
  });
  window.addEventListener("scroll", () => {
    if (window.ScrollLock && window.ScrollLock._holders.size > 0) return;
    // Skip while the mobile PDP parallax panel is dragging/snapping — that flow
    // drives window.scrollTo() every rAF frame, firing native scroll events at the
    // same rate. Writing this var independently on every one of those ticks fights
    // the panel's own header/reveal sync and reads as the announcement bar and
    // header flickering out of step with the panel while it closes on real iOS.
    if (typeof window._pdpParallaxIsBusy === 'function' && window._pdpParallaxIsBusy()) return;
    if(window.scrollY > 36){
        document.documentElement.style.setProperty('--anouncement-scrolling-height', `0px`);
        document.documentElement.style.setProperty('--anouncement-scroll-height', `0px`);
    }
    else{
      if (announcementBar) {
        document.documentElement.style.setProperty('--anouncement-scrolling-height', `${announcementBar.offsetHeight}px`);
        document.documentElement.style.setProperty('--anouncement-scroll-height', `${announcementBar.offsetHeight}px`);
      }
    }
  });

}

// Init on page load
document.addEventListener('DOMContentLoaded', initAnnouncementSwipers);

// Enable live updates inside Shopify editor
if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('.js-announcement-carousel')) {
      initAnnouncementSwipers();
    }
  });

  document.addEventListener('shopify:section:select', (event) => {
    if (event.target.querySelector('.js-announcement-carousel')) {
      initAnnouncementSwipers();
    }
  });

  document.addEventListener('shopify:block:select', (event) => {
    if (event.target.closest('.js-announcement-carousel')) {
      initAnnouncementSwipers();
    }
  });
}

// Lazy-load banner videos: only fetch/play the video that is actually
// visible on screen (correct breakpoint variant), not every hidden one.
function initLazyFocalVideos() {
  const videos = document.querySelectorAll('video.lazy-focal-video[data-src]');
  if (!videos.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const video = entry.target;
      if (!video.src && video.dataset.src) {
        video.src = video.dataset.src;
        video.load();
      }
      video.play().catch(() => {});
      observer.unobserve(video);
    });
  }, { rootMargin: '200px 0px', threshold: 0.01 });

  videos.forEach((video) => observer.observe(video));
}

document.addEventListener('DOMContentLoaded', initLazyFocalVideos);

if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('.lazy-focal-video')) {
      initLazyFocalVideos();
    }
  });
}

class CountdownTimer extends HTMLElement {
  connectedCallback() {
    this.countdownElement = this.querySelector('.announcement-bar__countdown');
    if (!this.countdownElement) return;

    const countdownDateStr = this.countdownElement.dataset.countdownDate;
    const countdownTimerStr = this.countdownElement.dataset.countdownTime;
    if (!countdownDateStr && !countdownTimerStr) return;
    const [month, day, year] = countdownDateStr.trim().split('-'); 
    const formattedDate = `${year}-${month}-${day}`;
    const fullDateTime = `${formattedDate}T${countdownTimerStr}`;
    this.countdownDate = new Date(fullDateTime).getTime();
    this.countdownTimer = this.countdownElement.querySelector('.countdown-timer');
    this.countdownText = this.countdownElement.querySelector('.countdown-text');
    this.countdownExpired = this.countdownElement.querySelector('.countdown-expired');
    this.bannerLink = this.countdownElement.querySelector('.banner_link');
    this.slide_link = this.countdownElement.querySelector('.slide-link');

    this.updateCountdown();
    this.intervalId = setInterval(() => this.updateCountdown(), 1000);
  }

  disconnectedCallback() {
    if (this.intervalId) clearInterval(this.intervalId);
  }

  updateCountdown() {
    const now = new Date().getTime();
    const distance = this.countdownDate - now;

    if (distance < 0) {
      if (this.countdownTimer) this.countdownTimer.style.display = 'none';
      if (this.countdownText) this.countdownText.style.display = 'none';
      if (this.countdownExpired) this.countdownExpired.style.display = 'flex';
      if (this.bannerLink) this.bannerLink.style.display = 'none';
      if ( this.slide_link) this.slide_link.style.display = 'none';
      return;
    }

    if (this.countdownTimer) this.countdownTimer.style.display = 'flex';
    if( this.slide_link) this.slide_link.style.display = 'flex'

    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);

    if (isNaN(days) || isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
      const swiperSlide = this.closest('.swiper-slide');
      swiperSlide.remove();
      return;
    }

    this.countdownElement.querySelector('.countdown-days').textContent = days.toString().padStart(2,"0");
    this.countdownElement.querySelector('.countdown-hours').textContent = hours.toString().padStart(2,"0");
    this.countdownElement.querySelector('.countdown-minutes').textContent = minutes.toString().padStart(2,"0");
    this.countdownElement.querySelector('.countdown-seconds').textContent = seconds.toString().padStart(2,"0");
  }
}

customElements.define('countdown-timer', CountdownTimer);


class ScrollingPromotion extends HTMLElement {
	constructor() {
	  super();
  
	  this.config = {
		moveTime: parseFloat(this.dataset.speed), 
		space: 100, 
	  };
  
	  this.promotion = this.querySelector('.promotion');
   this.init();
	}
  
	init() {
	  if (this.childElementCount === 1) {
		this.promotion.classList.add('promotion--animated');
  
		for (let index = 0; index < 20; index++) {
		  this.clone = this.promotion.cloneNode(true);
		  this.clone.setAttribute('aria-hidden', true);
		  this.appendChild(this.clone);
  
		  const imageWrapper = this.clone.querySelector('.promotion__item');
		  if (imageWrapper) {
			imageWrapper.classList.remove('loading');
		  }
		}
  
		const animationTimeFrame = this.config.moveTime;
		this.style.setProperty('--duration', `${animationTimeFrame}s`);
  
		// pause when out of view
		const observer = new IntersectionObserver((entries, _observer) => {
		  entries.forEach(entry => {
			if (entry.isIntersecting) {
			  this.scrollingPlay();
			} else {
			  this.scrollingPause();
			}
		  });
		}, {rootMargin: '0px 0px 50px 0px'});
  
		observer.observe(this);
	  }
	}
  
	scrollingPlay() {
	  this.classList.remove('scrolling-promotion--paused');
	}
  
	scrollingPause() {
	  this.classList.add('scrolling-promotion--paused');
	}
  }
customElements.define('scrolling-promotion', ScrollingPromotion);

class AnimateAccordion extends HTMLElement {
  constructor() {
    super();
    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 200
    });
  }

  init() {
    this.details = this.querySelector(':scope > details');
    this.summary = this.details?.querySelector(':scope > summary');
    this.content = this.details?.querySelector('.accordion__content');

    if (!this.details || !this.summary || !this.content) return;

    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;

    this.summary.addEventListener('click', (e) => this.onClick(e));
  }

  onClick(e) {
    e.preventDefault();
    e.stopPropagation();

    this.content.style.overflow = 'hidden';

    if (this.isClosing || !this.details.open) {
      this.closeOtherAccordions();
    } else if (this.isExpanding || this.details.open) {
      this.shrink();
      const name = this.details?.getAttribute('name');
       if(name == "footer-accordion"){
         setTimeout(() => {
            updateFooterPadding();
          }, 200);
       }
    }
  }

  closeOtherAccordions() {
    const name = this.details?.getAttribute('name');
    document.querySelectorAll(`details[name="${name}"]`).forEach(acc => {
      if (acc !== this.details && acc.open) {
          const accordion = acc.closest('acordion-animated');
          requestAnimationFrame(() => accordion.shrink());
      }
    });
  
    setTimeout(() => this.open(), 150);
  }
  shrink() {
    this.isClosing = true;
    
    const startHeight = `${this.content.offsetHeight}px`;
    const endHeight = `0px`;
    const startOpacity = 1;
    const endOpacity = 0;

    if (this.animation) this.animation.cancel();

    this.animation = this.content.animate({
      height: [startHeight, endHeight],
      opacity: [startOpacity, endOpacity]
    }, {
      duration: 300,
      easing: 'ease'
    });

    this.animation.onfinish = () => this.onAnimationFinish(false);
    this.animation.oncancel = () => this.isClosing = false;
    

    this.summary.setAttribute('aria-expanded', false);
  }

  open() {
    this.details.open = true;
    requestAnimationFrame(() => this.expand());
    const name = this.details?.getAttribute('name');
    if(name == "footer-accordion"){
      updateFooterPadding();
    }
  }

  expand() {
    this.isExpanding = true;
    const startHeight = `0px`;
    const endHeight = `${this.content.offsetHeight}px`;
    const startOpacity = 0;
    const endOpacity = 1;

    if (this.animation) this.animation.cancel();

    this.animation = this.content.animate({
      height: [startHeight, endHeight],
      opacity: [startOpacity, endOpacity]
    }, {
      duration: 300,
      easing: 'ease-out'
    });

    this.animation.onfinish = () => this.onAnimationFinish(true);
    this.animation.oncancel = () => this.isExpanding = false;
    this.summary.setAttribute('aria-expanded', true);
  }

  onAnimationFinish(open) {
    this.details.open = open;
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.content.style.height = '';
    this.content.style.overflow = '';
  }
}

customElements.define('acordion-animated', AnimateAccordion);

class CustomTags extends HTMLElement {
  constructor() {
    super();
    const custom_tag_selector = this.querySelector("#customer_tags");
    const email_tag = this.querySelector("#email_marketing");
    const prefrence_tag = this.querySelectorAll('input[name=gender]');
    const checkbox_alert = this.querySelector('.checkbox--alert');
    const submitButton = this.querySelector('.footeremailbutton');
    const emailInput = this.querySelector('input[name="contact[email]"]');
    const emailError = this.querySelector('.email--error');
    this.validateCheckbox(checkbox_alert, submitButton, email_tag, emailInput, emailError);
    this.emailMarketing(email_tag, custom_tag_selector);
    this.PrefrenceSelection(prefrence_tag, custom_tag_selector, email_tag);
  }

  validateCheckbox(checkbox_alert, submitButton, email_tag, emailInput, emailError) {
    submitButton.addEventListener('click', (e) => {
      let hasError = false;
      
      // Email validation
      if (emailInput) {
        const email = emailInput.value.trim();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        
        if (!email) {
          if (emailError) {
            emailError.classList.remove('hidden');
          }
          hasError = true;
        } else if (!emailRegex.test(email)) {
          if (emailError) {
            emailError.classList.remove('hidden');
          }
          hasError = true;
        } else {
          if (emailError) {
            emailError.classList.add('hidden');
          }
        }
      }
      
      // Checkbox validation
      if (email_tag && !email_tag.checked) {
        if (checkbox_alert) {
          checkbox_alert.classList.remove('hidden');
        }
        hasError = true;
      } else {
        if (checkbox_alert) {
          checkbox_alert.classList.add('hidden');
        }
      }
      if (hasError) {
        e.preventDefault();
      }
    });
  }
  emailMarketing(email_tag, custom_tag_selector) {
    if (email_tag && custom_tag_selector) {
      email_tag.addEventListener('change', () => {
        // if(!checkbox_alert.classList.contains('hidden')){checkbox_alert.classList.remove('hidden');}
        let currentTags = custom_tag_selector.value.trim();
        if (email_tag.checked) {
          if (currentTags.length > 0) {
            if (!currentTags.includes(email_tag.value)) {
              custom_tag_selector.value = currentTags + ', ' + email_tag.value;
            }
          } else {
            custom_tag_selector.value = email_tag.value;
          }
        } else {
          if (currentTags.includes(email_tag.value)) {
            let tagsArray = currentTags.split(',').map(tag => tag.trim());
            tagsArray = tagsArray.filter(tag => tag !== email_tag.value);
            custom_tag_selector.value = tagsArray.join(',');
          }
        }
      });
    }
  }

  PrefrenceSelection(prefrence_tag, custom_tag_selector) {
    if (prefrence_tag && custom_tag_selector) {
      prefrence_tag.forEach(radio => {
        radio.addEventListener('change', function () {
          const selectedGender = this.value.trim();
          let currentTags = custom_tag_selector.value.trim();
          let tagsArray = currentTags.length > 0
            ? currentTags.split(',').map(tag => tag.trim())
            : [];
          const optionValues = Array.from(prefrence_tag).map(option => option.value.trim());
          
          tagsArray = tagsArray.filter(tag => !optionValues.includes(tag));
          if (selectedGender && !tagsArray.includes(selectedGender)) {
            tagsArray.push(selectedGender);
          }
          custom_tag_selector.value = tagsArray.join(',');
        });
      });
    }
  }
}

customElements.define('custom-tags', CustomTags);
const countrySelector = document.querySelectorAll(".country-popup__opener");
if (countrySelector) {
  countrySelector.forEach((el) => {
    el.addEventListener('click', (e) => {
      e.preventDefault();
      const modal = document.querySelector('#md-app-embed__modal');
      if (modal) {
        modal.style.display = 'block';
      }
    });
  });
}

function updateFooterPadding() {
 const mainFooterSection = document.querySelector(".shopify-section-group-footer-group");
  const footerSection = document.querySelector('footer');
      // Get and log the variable
    const accordionHeightVar = getComputedStyle(document.documentElement)
      .getPropertyValue('--accordion-height')
      .trim();
    // console.log('CSS Variable --accordion-height:', accordionHeightVar);
    

  if (mainFooterSection && footerSection) {
    const paddingBlock = footerSection.offsetHeight;
    mainFooterSection.style.setProperty('--padding-block', (paddingBlock / 2) + 'px');

    document.querySelectorAll("details[name='footer-accordion']").forEach((accordion) => {
      accordion.addEventListener("toggle", () => {
        const accordionContent = accordion.querySelector('.accordion__content.footer-menu-links');
        const summaryElement = accordion.querySelector('summary');
        if (!accordionContent || !summaryElement) return;

        const accordionHeight = accordionContent.offsetHeight;
        document.documentElement.style.setProperty('--accordion-height', accordionHeight + 'px');

        // Check actual open state (not string)
        const isOpen = summaryElement.getAttribute('aria-expanded') === 'true';

        if (isOpen) {
            const scrollToBottom = window.scrollY + accordionContent.getBoundingClientRect().height;
            window.scrollTo({ top: scrollToBottom, behavior: 'smooth' });
        } else {
          // const scrollToBottom = window.scrollY - accordionContent.getBoundingClientRect().height;
          // window.scrollTo({ top: scrollToBottom, behavior: 'smooth' });
        }
      });
    });
  }

}
// section-footer.css is loaded with media="print" onload="this.media='all'", so
// it is not applied yet when this deferred script runs. The footer is fixed
// (position: fixed; bottom: 0) from first paint via critical inline CSS in
// footer.liquid, so it never jumps into the viewport later - only its height
// (and therefore this spacer var) settles once the real geometry is known.
const footerStylesheet = document.querySelector('link[data-footer-css]');
if (footerStylesheet && !footerStylesheet.sheet) {
  footerStylesheet.addEventListener('load', updateFooterPadding, { once: true });
  // Still initialise if the sheet 404s, otherwise the footer never gets its
  // padding or parallax class at all.
  footerStylesheet.addEventListener('error', updateFooterPadding, { once: true });
} else {
  updateFooterPadding();
}
window.addEventListener('resize', updateFooterPadding);

const footer = document.querySelector('footer');
const maxTranslatePercent = 25;

function updateFooterPosition() {
    if (!footer) return;

    const mainFooterSection = document.querySelector(".shopify-section-group-footer-group");
    if (!mainFooterSection) return;

    const rect = mainFooterSection.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    const footerHeight = rect.height;
    const footerTop = rect.top;

    // How much of footer is visible
    const visibleHeight = Math.min(footerHeight, windowHeight - footerTop);
    const clampedVisibleHeight = Math.max(0, visibleHeight);
    const visiblePercent = clampedVisibleHeight / footerHeight;

    let translateY = 0;
    let translatePercent = 0.6;
    if(window.innerWidth < 768){
      translatePercent = .9;
    }
    // Only translate when visiblePercent is between 0% and 50%
    if (visiblePercent > 0 && visiblePercent < translatePercent) {
        const invertedVisible = (translatePercent  - visiblePercent) / translatePercent;  // 1 when at 0%, 0 when at 50%
        const easedProgress = invertedVisible * invertedVisible;  // Quadratic easing
        translateY = easedProgress * maxTranslatePercent;
    }
    footer.style.transform = `translateY(${translateY}%)`;
}

window.addEventListener('load', updateFooterPosition);
window.addEventListener('resize', updateFooterPosition);

let ticking = false;
window.addEventListener('scroll', () => {
    if (!ticking) {
        window.requestAnimationFrame(() => {
            updateFooterPosition();
            ticking = false;
        });
        ticking = true;
    }
});



// Shared across every <svg-image> instance on the page, keyed by URL. The
// footer alone puts up to 5 of these on the page (logo + 4 social icons); the
// header/PDP put a few more. Each used to run its own independent fetch() in
// the constructor with no cache, so any URL that appears twice (e.g. the same
// logo in both the default and alternate header, connected at the same time)
// was downloaded and parsed twice for byte-identical content.
const svgImageFetchCache = new Map();

function fetchSvgImage(url) {
  if (!svgImageFetchCache.has(url)) {
    svgImageFetchCache.set(
      url,
      fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error('Network response was not ok');
          return response.text();
        })
        .catch((error) => {
          console.error('Error fetching SVG:', error);
          return null;
        })
    );
  }
  return svgImageFetchCache.get(url);
}

class SvgImage extends HTMLElement {
  connectedCallback() {
    // connectedCallback (not the constructor) - it can re-fire if the element
    // is moved in the DOM, so guard against loading twice.
    if (this._svgImageStarted) return;
    this._svgImageStarted = true;

    const mainDiv = this.querySelector('.collection-logo');
    const imageUrl = mainDiv?.getAttribute('data-image-url');
    if (!imageUrl) return;

    const load = () => {
      fetchSvgImage(imageUrl).then((svgText) => {
        if (svgText != null) mainDiv.innerHTML = svgText;
      });
    };

    // Instances already in (or near) the viewport at load - the header logo,
    // a PDP thumbnail - load immediately, same as before. Instances that
    // start off-screen - every one of them in the footer, on first paint of
    // any page - wait until the user scrolls near them, so their network
    // requests don't compete with the initial critical-path fetches at all.
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          load();
        }
      }, { rootMargin: '200px' });
      observer.observe(this);
    } else {
      load();
    }
  }
}
customElements.define('svg-image', SvgImage);


function showPreloader() {
    const preloader = document.getElementById('preloader');
    if (preloader) {
      preloader.style.display = 'block';
      preloader.style.visibility = 'visible';
      preloader.style.opacity = '1';
      // Hide the rest of the page content
      document.body.style.overflow = 'hidden';
    }
  }
document.addEventListener('DOMContentLoaded', function () {

  const form = document.querySelector('customer-signin #create_customer');
  const dataUrl = document.querySelector("customer-signin")?.getAttribute("data-url");

  if (form && dataUrl) {
    form.addEventListener('submit', function () {
      sessionStorage.setItem('previousPage', window.location.href);
      sessionStorage.setItem('dataUrl', dataUrl);
    });
  }

  const previousPage = sessionStorage.getItem('previousPage');
  const previousData = sessionStorage.getItem("dataUrl");
  if ( window.location.href.includes("/register") && previousPage != null && previousData != null) {
    sessionStorage.removeItem('previousPage');
    sessionStorage.removeItem('dataUrl');
    showPreloader();
    setTimeout(() => {
      if(previousPage.includes("?")){

          window.location.href = previousPage+"&success=0"; 
      }
      else{
        window.location.href = previousPage+"?success=0";
      }
    }, 100);
  }
  else if( (window.location.href.includes("customer_posted") || window.location.href == "https://nude-project.com/account" || window.location.href == "https://nude-project.com/" || window.location.href == "https://nude-project.com/es")  && previousPage != null && previousData != null) {
    sessionStorage.removeItem('previousPage');
    sessionStorage.removeItem('dataUrl');
    sessionStorage.setItem('hasAccount', true);
    // sessionStorage.setItem('has_posted', true);
    showPreloader();
    setTimeout(() => {
       if(previousPage.includes("?")){
        window.location.href = previousPage+"&success=1";
       }
      else{
        window.location.href = previousPage+"?success=1"; 
      }
    }, 100); 
  }
});




// ----------------------header js --------------------------

function applyHeaderLogic() {
  const headerWrapper = document.querySelector('.header-wrapper');
  if (!headerWrapper) return;

  // reset
  headerWrapper.classList.remove('transprent-header', 'transprent-header-big');

  // first visible section
  let firstSection = document.querySelector('main > .shopify-section')?.querySelector('.header-version');

  // No banner section declaring a header version (e.g. content/legal pages) →
  // keep the header solid. A transparent header only makes sense over a hero
  // banner; on a plain white page its transparent text color is invisible.
  if (!firstSection) {
    headerWrapper.classList.add('header-ready');
    return;
  }

  const headerVersion = firstSection.dataset.headerVersion;

  if (headerVersion === "transprent") {
    headerWrapper.classList.add('transprent-header');
  } else if (headerVersion === "transparent_big") {
    headerWrapper.classList.add('transprent-header', 'transprent-header-big');
  }
  headerWrapper.classList.add('header-ready');
}


// run on load
document.addEventListener("DOMContentLoaded", applyHeaderLogic);

// run again in theme editor
document.addEventListener("shopify:section:load", applyHeaderLogic);
document.addEventListener("shopify:section:select", applyHeaderLogic);


if(!customElements.get('swym-header-icon')){
  class SwymHeaderIcon extends HTMLElement{
    constructor(){
      super();
      if (!window.SwymCallbacks) window.SwymCallbacks = [];
      window.SwymCallbacks.push(this._initializeSwymButton.bind(this));
      
      // Setup counter parent click listeners
      this._setupCounterParentListeners();
      
      // Watch for wishlist grid and delete buttons
      this._observeWishlistGrid();
    }

    _setupCounterParentListeners(){
      const counterParents = document.querySelectorAll("swymcs-wishlist-button button");
      
      if(counterParents.length > 0){
        counterParents.forEach(counter => {
          counter.addEventListener('click', () => {
            setTimeout(() => {
              // Re-initialize to update counter
              if(window._swat){
                this._initializeSwymButton(window._swat);
              }
            }, 500);
          });
        });
      }
    }

    _observeWishlistGrid(){
      const observer = new MutationObserver((mutations) => {
        const wishlistGrid = document.querySelector('.swym-wishlist-grid');
        
        if (wishlistGrid) {
          console.log('swym-wishlist-grid is added!');
          
          // Now watch for delete buttons
          this._setupDeleteButtonListeners();
          
          // Keep observing for dynamically added delete buttons
          const deleteObserver = new MutationObserver(() => {
            this._setupDeleteButtonListeners();
          });
          
          deleteObserver.observe(wishlistGrid, {
            childList: true,
            subtree: true
          });
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    _setupDeleteButtonListeners(){
      const deleteButtons = document.querySelectorAll(".swym-delete-btn");
      
      if(deleteButtons.length > 0){
        console.log(deleteButtons, "Delete buttons found");
        
        deleteButtons.forEach(btn => {
          // Remove existing listener to avoid duplicates
          btn.removeEventListener('click', this._handleDelete);
          
          // Add click listener
          btn.addEventListener('click', this._handleDelete.bind(this));
        });
      }
    }

    _handleDelete(){
      console.log('Delete button clicked');
      
      // Wait for deletion to complete, then update counter
      setTimeout(() => {
        if(window._swat){
          this._initializeSwymButton(window._swat);
          
          // Also update all counter parents
          const counterParents = document.querySelectorAll("swymcs-wishlist-button button");
          counterParents.forEach(async (button) => {
            const counter = button.querySelector('.swym-wishlist-header-counter');
            if(counter){
              try {
                const listData = await this.getListsData(window._swat);
                const count = await this._getValidProductCount(listData);
                counter.innerHTML = "(" + count + ")";
              } catch (error) {
                console.error('Error updating counter:', error);
              }
            }
          });
        }
      }, 500);
    }

    async _initializeSwymButton(swat){
      this.button = this.querySelector('a') || this.querySelector('button');
      this.counter = this.querySelector(".swym-wishlist-header-counter");

      if(this.counter){
        await this._updateCounter(swat);

        if (!this._swymListenersAttached) {
          this._swymListenersAttached = true;
          swat.evtLayer.addEventListener(swat.JSEvents.addedToWishlist, () => {
            this._updateCounter(swat);
          });
          swat.evtLayer.addEventListener(swat.JSEvents.removedFromWishlist, () => {
            this._updateCounter(swat);
          });
        }
      }
    }

    async _updateCounter(swat){
      try {
        const listData = await this.getListsData(swat);
        const count = await this._getValidProductCount(listData);
        if(count > 0){
          this.counter.innerHTML = "(" + count + ")";
          this.counter.style.display = 'flex';
          this.counter.parentElement.style.display = 'flex';
          this.counter.parentElement.classList.remove("hidden");
        } else {
          this.counter.innerHTML = " ";
          this.counter.style.display = 'none';
          this.counter.parentElement.style.display = 'none';
          this.counter.parentElement.classList.add("hidden");
        }
      } catch (error) {
        console.error('Error fetching wishlist data:', error);
        this.counter.innerHTML = " ";
        this.counter.style.display = 'none';
        this.counter.parentElement.style.display = 'none';
        this.counter.parentElement.classList.add("hidden");
      }
    }

    getListsData(swat){
      return new Promise((resolve, reject) => {
        swat.fetchLists({
          callbackFn: data => resolve(data),
          onError: error => reject(error)
        });
      });
    }

    // Only counts items whose wishlisted variant still exists on the live product,
    // so the count matches what the wishlist page actually renders.
    async _getValidProductCount(listData){
      let products = {};
      for (const list of listData) {
        for (const product of list.listcontents) {
          const epi = product.epi;
          if (!products[epi]) {
            products[epi] = product;
          }
        }
      }
      const uniqueProducts = Object.values(products);
      const validityChecks = await Promise.all(uniqueProducts.map(async (product) => {
        try {
          const productHandleString = product.du.split('/products/')[1];
          const productHandle = productHandleString.split('?')[0];
          const response = await fetch(`/products/${productHandle}.js`);
          const jsonData = await response.json();
          return jsonData.variants.some(variant => variant.id === product.epi);
        } catch (error) {
          return false;
        }
      }));
      return validityChecks.filter(Boolean).length;
    }
  }
  customElements.define('swym-header-icon', SwymHeaderIcon);
}



document.querySelectorAll('.accordion-header').forEach(header => {
  header.addEventListener('click', () => {
    // Toggle open class on accordion
    const item = header.closest('.accordion-item');
    item.classList.toggle('open');

    // Wait for transition to complete, then update padding
    setTimeout(() => {
      const footer = document.querySelector('.shopify-section-group-footer-group');
      const footerHeight = footer.scrollHeight;

      // Add some dynamic padding based on footer height
      footer.style.setProperty('--padding-block', `${footerHeight * 0.05}px`);
    }, 300); // matches accordion transition time
  });
});


document.addEventListener('DOMContentLoaded', () => {
  const updateKlarnaOverlay = () => {
    const klarnaRoot = document.querySelector('#klarna-fullscreen-shadow-dom');

    if (klarnaRoot && klarnaRoot.shadowRoot) {
      const overlay = klarnaRoot.shadowRoot.querySelector('.overlay');

      if (overlay) {
        overlay.style.backgroundColor = '#4829224d';
        overlay.style.backgroundImage = 'none';
      }
    }
  };

  updateKlarnaOverlay();

  const observer = new MutationObserver(() => {
    updateKlarnaOverlay();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
});


// Tracking page Js Start

document.addEventListener('DOMContentLoaded', () => {
  const sel = (s) => document.querySelector(s);
  const observerOpts = { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] };
  let waiting = false;

  const updatePlaceholders = () => {
    document.querySelectorAll('.shipup-text-field-container input').forEach(input => {
      if (input.placeholder === 'john@mail.com') input.placeholder = 'Email Address';
      if (input.placeholder === '75001') input.placeholder = 'Zip Code/Postcode';
      if (input.placeholder === 'ABCD1234') input.placeholder = 'Tracking Number';
    });
    const e = sel('.shipup-reset-button.shipup-tracker-search-type-selector-1');
    const t = sel('.shipup-reset-button.shipup-tracker-search-type-selector-2');
    if (e && e.textContent.trim() !== 'By Email Address') e.textContent = 'By Email Address';
    if (t && t.textContent.trim() !== 'By Tracking Number') t.textContent = 'By Tracking Number';
  };

  const isTrackingActive = () => sel('.shipup-reset-button.shipup-tracker-search-type-selector-2')?.classList.contains('active');

  const clearTrackingErrors = () => {
    // Hide only - these nodes are managed by Shipup's own renderer, and detaching
    // them with .remove() breaks its reconciliation and crashes the whole widget.
    document
      .querySelectorAll('.shipup-top-navbar-error, .shipup-text-field-error')
      .forEach(err => err.style.setProperty('display', 'none', 'important'));
  };

  const observer = new MutationObserver(() => {
    updatePlaceholders();
    if (!waiting) return;
    const emailBtn = sel('.shipup-reset-button.shipup-tracker-search-type-selector-1');
    if (!emailBtn?.classList.contains('active')) return;
    waiting = false;
    observer.disconnect();
    sel('.shipup-reset-button.shipup-tracker-search-type-selector-2')?.click();
    sel('.shipup-tracker-search').style.visibility = '';
    observer.observe(document.body, observerOpts);
  });

  document.addEventListener('click', (e) => {
    if (e.target.closest('.shipup-tracker-search-submit') && isTrackingActive()) {
      waiting = true;
      sel('.shipup-tracker-search').style.visibility = 'hidden';
      setTimeout(() => { if (waiting) { waiting = false; sel('.shipup-tracker-search').style.visibility = ''; } }, 4000);
    }
    if (e.isTrusted && e.target.closest('.shipup-reset-button.shipup-tracker-search-type-selector-1, .shipup-reset-button.shipup-tracker-search-type-selector-2')) {
      clearTrackingErrors();
    }
  }, true);

  updatePlaceholders();
  observer.observe(document.body, observerOpts);
});

// Header account icon: on the wishlist page only, if clicked while logged out
// (href points at /account/login - the theme swaps it to routes.account_url
// once logged in), remember the current page so main-account.liquid can send
// the customer back here after login. global.js loads on every page, so the
// listener itself is only ever attached when <swym-wishlist-page> is present -
// other pages (PDP, etc.) don't get this listener at all, not even a no-op one.
if (document.querySelector('swym-wishlist-page')) {
  document.querySelectorAll('.header__icon--account').forEach((link) => {
    link.addEventListener('click', () => {
      try {
        const linkUrl = new URL(link.href, window.location.origin);
        if (linkUrl.pathname !== '/account/login') return;
        sessionStorage.setItem('postLoginRedirect', window.location.pathname);
      } catch (err) {}
    });
  });
}

// Tracking page Js End